/**
 * Star Rating Hooks (Engine A)
 *
 * React Query hooks for the persisted `property_star_ratings` and the on-demand
 * `star-engine` rate/re-rate mutation. Mirrors the `usePropertyValuations`
 * query-keys-factory + query/mutation-invalidation pattern.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as starRatingService from '@/services/star-rating.service';
import { propertyKeys } from '@/hooks/useProperties';

export const starRatingKeys = {
  all: ['starRatings'] as const,
  byProperty: (propertyId: string) => [...starRatingKeys.all, 'property', propertyId] as const,
};

/** Latest persisted rating for a property (null until first rated). */
export function useStarRating(propertyId: string | undefined) {
  return useQuery({
    queryKey: starRatingKeys.byProperty(propertyId!),
    queryFn: () => starRatingService.getLatestRating(propertyId!),
    enabled: !!propertyId,
  });
}

/**
 * Rate / re-rate a property via the `star-engine` edge function. On success,
 * refreshes this property's rating query plus the property detail/list queries
 * (the run also updates the denormalized `star_*` columns on `properties`).
 */
export function useRateProperty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ propertyId, force }: { propertyId: string; force?: boolean }) =>
      starRatingService.rateProperty(propertyId, force),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: starRatingKeys.byProperty(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: propertyKeys.detail(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
    },
  });
}

/**
 * Rate an AI-import extraction as a candidate (web preview) via the
 * `star-engine` edge function. Deliberately performs NO cache invalidation: the
 * candidate branch persists nothing (no `property_star_ratings` row, no
 * `properties` denormalize), so there is no server state to refresh — the preview
 * rating lives only in the caller's state. Web save recomputes + persists by
 * `property_id` via `useRateProperty`.
 */
export function useRateCandidate() {
  return useMutation({
    mutationFn: (payload: starRatingService.CandidateRatingPayload) =>
      starRatingService.rateCandidate(payload),
  });
}

/**
 * Undo a whole star-engine run (per-run undo affordance on the panel). Reverses
 * the run's `group_id` unit via the shared `ai-command` undo endpoint, then
 * refreshes this property's rating plus the property detail/list queries (the
 * undo restores the previous denormalized `star_*` columns) and the AI change
 * log. `groupId` is the rating id; `propertyId` scopes the invalidation.
 */
export function useUndoRatingRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId }: { groupId: string; propertyId: string }) =>
      starRatingService.undoRatingRun(groupId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: starRatingKeys.byProperty(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: propertyKeys.detail(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['ai-change-log'] });
    },
  });
}
